import {
    Alert,
    Box,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { getAllDocumentType } from '../../../../data/api/document/DocumentRoutes';
import { getCompanies } from '../../../../data/api/users/UserRoutes';
import useIndex from '../../../../hooks/useIndex';
import validErrors from '../../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,
        transition: '0.25s ease',

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const Step1Parties = ({
    control,
    register,
    errors,
    templates,
    onTemplateChange,
    selectedTemplateId,
    isAdmin,
    selectedCompanyId,
    onCompanySelect,
}) => {
    const { token } = useIndex();

    const [documentTypes, setDocumentTypes] = useState([]);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        loadDocumentTypes();
        if (isAdmin) loadCompanies();
    }, [isAdmin]);

    const loadDocumentTypes = async () => {
        try {
            const { data } = await getAllDocumentType();
            setDocumentTypes(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const loadCompanies = async () => {
        try {
            const { data } = await getCompanies(token);
            if (data?.success) setCompanies(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const handleCompanyChange = (id) => {
        const company = companies.find((c) => c.id === id) ?? null;
        onCompanySelect?.(company);
    };

    return (
        <StepWrapper>
            <StepHeader>
                <Typography className="title">
                    Plantilla y partes del contrato
                </Typography>

                <Typography className="subtitle">
                    Selecciona la plantilla base y define los datos del vendedor, comprador y representante legal.
                </Typography>
            </StepHeader>

            <SectionCard>
                <SectionTitle>
                    <DescriptionOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Configuración inicial
                        </Typography>
                        <Typography className="section-subtitle">
                            Empresa contratante y plantilla del documento
                        </Typography>
                    </Box>
                </SectionTitle>

                <Grid container spacing={2}>
                    {isAdmin && (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small" sx={fieldStyles}>
                                    <InputLabel id="company_label">Empresa contratante</InputLabel>

                                    <Select
                                        labelId="company_label"
                                        label="Empresa contratante"
                                        value={selectedCompanyId ?? ''}
                                        onChange={(e) => handleCompanyChange(e.target.value || null)}
                                    >
                                        <MenuItem value="">
                                            <em>Sin seleccionar — completar manualmente</em>
                                        </MenuItem>

                                        {companies.map((c) => (
                                            <MenuItem key={c.uuid} value={c.id}>
                                                {c.name} {c.document ? `· ${c.document}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {!selectedCompanyId && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity="info"
                                        sx={{
                                            borderRadius: '14px',
                                            background: 'rgba(16, 40, 95, 0.06)',
                                            color: '#10285f',
                                            border: '1px solid rgba(16, 40, 95, 0.12)',
                                            '& .MuiAlert-icon': {
                                                color: '#d6aa3a',
                                            },
                                        }}
                                    >
                                        Selecciona una empresa registrada o llena los datos manualmente. La empresa elegida será la que pueda firmar el contrato y a quien se le entregarán los códigos al aprobarlo.
                                    </Alert>
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item xs={12}>
                        <FormControl fullWidth size="small" sx={fieldStyles}>
                            <InputLabel id="template_label">Plantilla (opcional)</InputLabel>

                            <Select
                                labelId="template_label"
                                label="Plantilla (opcional)"
                                value={selectedTemplateId ?? ''}
                                onChange={(e) => onTemplateChange?.(e.target.value || null)}
                            >
                                <MenuItem value="">
                                    <em>Sin plantilla — escribir desde cero</em>
                                </MenuItem>

                                {templates?.map((t) => (
                                    <MenuItem key={t.uuid} value={t.id}>
                                        {t.name}{t.requires_moves === false ? ' (sin entrega de jugadas)' : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard>
                <SectionTitle>
                    <StorefrontOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Vendedor
                        </Typography>
                        <Typography className="section-subtitle">
                            Datos de Fenabocol
                        </Typography>
                    </Box>
                </SectionTitle>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nombre"
                            defaultValue="Fenabocol"
                            error={!!errors.fbc_name}
                            helperText={errors.fbc_name?.message}
                            sx={fieldStyles}
                            {...register('fbc_name', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Correo"
                            defaultValue="fenabocolorg@gmail.com"
                            error={!!errors.fbc_email}
                            helperText={errors.fbc_email?.message}
                            sx={fieldStyles}
                            {...register('fbc_email', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dirección"
                            defaultValue="Calle 43a sur #78g - 28 Bogotá"
                            error={!!errors.fbc_address}
                            helperText={errors.fbc_address?.message}
                            sx={fieldStyles}
                            {...register('fbc_address', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Teléfono"
                            defaultValue="3002910614"
                            error={!!errors.fbc_phone}
                            helperText={errors.fbc_phone?.message}
                            sx={fieldStyles}
                            {...register('fbc_phone', { required: 'Obligatorio.' })}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard>
                <SectionTitle>
                    <BusinessOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Comprador
                        </Typography>
                        <Typography className="section-subtitle">
                            Datos de la empresa contratante
                        </Typography>
                    </Box>
                </SectionTitle>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nombre"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.company_name}
                            helperText={errors.company_name?.message}
                            sx={fieldStyles}
                            {...register('company_name', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Correo"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.company_email}
                            helperText={errors.company_email?.message}
                            sx={fieldStyles}
                            {...register('company_email', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dirección"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.company_address}
                            helperText={errors.company_address?.message}
                            sx={fieldStyles}
                            {...register('company_address', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Teléfono"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.company_phone}
                            helperText={errors.company_phone?.message}
                            sx={fieldStyles}
                            {...register('company_phone', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <FormControl fullWidth size="small" error={!!errors.document_type_id} sx={fieldStyles}>
                            <InputLabel id="doc_type_label">Tipo doc.</InputLabel>

                            <Controller
                                control={control}
                                name="document_type_id"
                                rules={{ required: 'Obligatorio.' }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        labelId="doc_type_label"
                                        label="Tipo doc."
                                        value={field.value ?? ''}
                                    >
                                        {documentTypes.map((dt) => (
                                            <MenuItem key={dt.id} value={dt.id}>
                                                {dt.short_name} - {dt.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />

                            {errors.document_type_id && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                    {errors.document_type_id.message}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={7}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Documento"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.company_document}
                            helperText={errors.company_document?.message}
                            sx={fieldStyles}
                            {...register('company_document', { required: 'Obligatorio.' })}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard>
                <SectionTitle>
                    <BadgeOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Representante legal
                        </Typography>
                        <Typography className="section-subtitle">
                            Persona autorizada para firmar por la empresa
                        </Typography>
                    </Box>
                </SectionTitle>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={7}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nombre del representante"
                            error={!!errors.signatory_name}
                            helperText={errors.signatory_name?.message}
                            sx={fieldStyles}
                            {...register('signatory_name', { required: 'Obligatorio.' })}
                        />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Cargo"
                            error={!!errors.signatory_position}
                            helperText={errors.signatory_position?.message}
                            sx={fieldStyles}
                            {...register('signatory_position', { required: 'Obligatorio.' })}
                        />
                    </Grid>
                </Grid>
            </SectionCard>
        </StepWrapper>
    );
};

export default Step1Parties;

const StepWrapper = styled(Box)`
    width: 100%;
`;

const StepHeader = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
    }

    .subtitle {
        margin-top: 0.2rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const SectionCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: rgba(16, 40, 95, 0.025);

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        color: #d6aa3a;
        font-size: 1.45rem;
    }

    .section-title {
        color: #10285f;
        font-size: 1rem;
        font-weight: 900;
        line-height: 1.2;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.8rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;